/**
 * 🔧 API TEST PAGE
 * Test page for verifying real API integration with base64 image handling
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Paper
} from '@mui/material';
import { verificationService } from '@/services/verificationService';
import { adminAuthHeaders } from '../../utils/adminAuthHeaders';

const ApiTestPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState('VER_1757945832073_s0b4an');

  // Test GET /api/admin/verification-requests
  const testGetVerifications = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await verificationService.getAdminVerificationRequests();
      setResult(response);
    } catch (err) {
      console.error('❌ API Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Test direct fetch to API
  const testDirectFetch = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch('https://polarride-api-17dr.vercel.app/api/admin/verification-requests', { headers: adminAuthHeaders() });
      const data = await response.json();
      setResult({ direct: data, response: { status: response.status, ok: response.ok } });
    } catch (err) {
      console.error('❌ Direct API Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Test approve/reject action
  const testApproveAction = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await verificationService.updateVerificationRequestStatus(
        verificationId,
        'approve',
        'admin-123',
        'Test approval from frontend'
      );
      setResult(response);
    } catch (err) {
      console.error('❌ Approve Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Render large base64 image properly
  const renderImage = (imageUrl: string, label: string) => {
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    if (!imageUrl || !imageUrl.startsWith('data:image/')) {
      return (
        <Alert severity="info">
          {label}: Not a valid base64 image URL
        </Alert>
      );
    }

    return (
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          {label} ({Math.round(imageUrl.length / 1024)}KB)
        </Typography>
        <Paper sx={{ p: 2, position: 'relative', height: 200 }}>
          {imageLoading && !imageError && (
            <Box 
              position="absolute" 
              top="50%" 
              left="50%" 
              sx={{ transform: 'translate(-50%, -50%)' }}
            >
              <CircularProgress size={30} />
              <Typography variant="caption" display="block" textAlign="center" mt={1}>
                Loading large image...
              </Typography>
            </Box>
          )}
          <img
            src={imageUrl}
            alt={label}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: imageLoading ? 'none' : 'block'
            }}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
          />
          {imageError && (
            <Alert severity="error">
              Failed to load {label.toLowerCase()}
            </Alert>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        🔧 API Integration Test Page
      </Typography>
      
      <Typography variant="body1" color="textSecondary" paragraph>
        Test the real API integration with large base64 images
      </Typography>

      <Grid container spacing={3}>
        {/* API Test Controls */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Test Controls
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  onClick={testGetVerifications}
                  disabled={loading}
                  fullWidth
                >
                  Test GET Verifications
                </Button>
                
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={testDirectFetch}
                  disabled={loading}
                  fullWidth
                >
                  Test Direct Fetch
                </Button>
                
                <TextField
                  label="Verification ID"
                  value={verificationId}
                  onChange={(e) => setVerificationId(e.target.value)}
                  size="small"
                  fullWidth
                />
                
                <Button
                  variant="contained"
                  color="success"
                  onClick={testApproveAction}
                  disabled={loading}
                  fullWidth
                >
                  Test Approve Action
                </Button>
              </Box>

              {loading && (
                <Box mt={2} textAlign="center">
                  <CircularProgress size={24} />
                  <Typography variant="caption" display="block" mt={1}>
                    Testing API...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Results Display */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Response
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {result && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Raw Response:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                    <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </Paper>
                  
                  {/* Display images if they exist in the response */}
                  {result.data?.data && result.data.data.length > 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Verification Images:
                      </Typography>
                      {result.data.data.map((verification: any, index: number) => (
                        <Box key={verification.id || index} mb={3}>
                          <Typography variant="subtitle1" gutterBottom>
                            {verification.userInfo?.firstName} {verification.userInfo?.lastName} - {verification.documentType}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              {renderImage(verification.documentImageUrl, 'Document Image')}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {renderImage(verification.selfieImageUrl, 'Selfie Image')}
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Instructions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 Test Instructions
          </Typography>
          <Typography variant="body2" paragraph>
            1. Click "Test GET Verifications" to fetch verification requests using the service
          </Typography>
          <Typography variant="body2" paragraph>
            2. Click "Test Direct Fetch" to test the raw API endpoint
          </Typography>
          <Typography variant="body2" paragraph>
            3. If you have a real verification ID (like VER_1757945832073_s0b4an), enter it and click "Test Approve Action"
          </Typography>
          <Typography variant="body2" paragraph>
            4. Large base64 images will be displayed with loading indicators
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Expected data structure for verification with large base64 images:
            </Typography>
            <Typography variant="caption" component="pre" sx={{ mt: 1 }}>
{`{
  "verificationId": "VER_1757945832073_s0b4an",
  "status": "pending_review", 
  "documentImageUrl": "data:image/jpeg;base64,/9j4AAQSkZJRgABA...",
  "selfieImageUrl": "data:image/jpeg;base64,iVBORw0KGgoAAAA..."
}`}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ApiTestPage;